import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../../api';
import './Home.css';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

function Home() {
    const [subcategories, setSubcategories] = useState([]);
    const [recentProducts, setRecentProducts] = useState([]);
    const [featuredProducts, setFeaturedProducts] = useState([]);

    // Tractor animation refs
    const canvasRef = useRef(null);
    const tractorRef = useRef(null);
    const animFrameRef = useRef(null);

    useEffect(() => {
        const fetchPloughParts = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/categories`);
                const all = res.data || [];
                const ploughParent = all.find(
                    (c) => c.name === 'Plough Parts' && c.parent_id === null
                );
                if (!ploughParent) return;
                const subs = all
                    .filter((c) => c.parent_id === ploughParent.id && c.enabled)
                    .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
                setSubcategories(subs);
            } catch (err) {
                console.error('Failed to fetch categories', err);
            }
        };

        const fetchRecentProducts = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/products/recent-machinery`);
                setRecentProducts((res.data || []).slice(0, 9));
            } catch (err) {
                console.error('Failed to fetch recent products', err);
            }
        };

        const fetchFeaturedProducts = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/products/featured`);
                setFeaturedProducts(res.data || []);
            } catch (err) {
                console.error('Failed to fetch featured products', err);
            }
        };

        fetchPloughParts();
        fetchRecentProducts();
        fetchFeaturedProducts();
    }, []);

    // ===== TRACTOR ANIMATION =====
    useEffect(() => {
        const canvas = canvasRef.current;
        const tractorImg = tractorRef.current;
        if (!canvas || !tractorImg) return;

        const ctx = canvas.getContext('2d');
        const TRACTOR_H = 56;
        let TRACTOR_W = Math.round(TRACTOR_H * 1.4); // recalculated once image loads
        const SPEED = 1.8;           // px per frame
        const DOT_INTERVAL = 22;     // px between dots
        const DOT_RADIUS = 4;
        const DOT_LIFE = 5;          // max dots visible at once before they fade/vanish
        const DOT_FADE_FRAMES = 40;  // frames to fade out each dot

        let tractorX = -160;
        let dots = [];               // { x, y, age }
        let lastDotX = -9999;
        let running = true;

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const groundY = canvas.height - 14; // dot Y position (ground level)
        const tractorY = canvas.height - TRACTOR_H - 8;

        const draw = () => {
            if (!running) return;

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Advance tractor
            tractorX += SPEED;
            if (tractorX > canvas.width + 160) {
                tractorX = -160;
                dots = [];
                lastDotX = -9999;
            }

            // Drop a dot every DOT_INTERVAL px
            if (tractorX - lastDotX >= DOT_INTERVAL) {
                dots.push({ x: tractorX - TRACTOR_W, y: groundY, age: 0 });
                lastDotX = tractorX;
                // Keep only last DOT_LIFE dots active; older ones just age
            }

            // Age all dots
            dots.forEach(d => d.age++);

            // Draw dots — only last DOT_LIFE, older ones fade
            dots.forEach((d, i) => {
                const rank = dots.length - 1 - i; // 0 = newest
                if (rank >= DOT_LIFE) {
                    // Start fading after DOT_LIFE
                    const fadeAge = d.age - (DOT_LIFE * (DOT_INTERVAL / SPEED));
                    if (fadeAge > DOT_FADE_FRAMES) return; // fully gone
                    const alpha = Math.max(0, 1 - fadeAge / DOT_FADE_FRAMES);
                    ctx.globalAlpha = alpha;
                } else {
                    ctx.globalAlpha = 1;
                }
                ctx.beginPath();
                ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
                ctx.fillStyle = '#888';
                ctx.fill();
            });

            // Prune fully invisible dots
            const maxAge = DOT_LIFE * (DOT_INTERVAL / SPEED) + DOT_FADE_FRAMES + 10;
            dots = dots.filter(d => d.age < maxAge);

            ctx.globalAlpha = 1;

            // Draw tractor
            ctx.drawImage(tractorImg, tractorX - TRACTOR_W, tractorY, TRACTOR_W, TRACTOR_H);

            animFrameRef.current = requestAnimationFrame(draw);
        };

        const startWhenLoaded = () => {
            if (tractorImg.naturalWidth && tractorImg.naturalHeight) {
                TRACTOR_W = Math.round((tractorImg.naturalWidth / tractorImg.naturalHeight) * TRACTOR_H);
            }
            animFrameRef.current = requestAnimationFrame(draw);
        };

        if (tractorImg.complete) {
            startWhenLoaded();
        } else {
            tractorImg.addEventListener('load', startWhenLoaded);
        }

        return () => {
            running = false;
            cancelAnimationFrame(animFrameRef.current);
            window.removeEventListener('resize', resize);
        };
    }, []);

    const ProductCard = ({ product, featured = false }) => {
        const cover = product.display_image
            || product.files?.find(f => f.isCover)?.url
            || product.files?.[0]?.url;
        return (
            <Link to={`/product/${product.id}`} className="recent-card">
                <div className={`recent-card-image${!cover ? ' recent-card-no-image-wrap' : ''}`}>
                    {cover ? (
                        <img src={cover} alt={product.name} />
                    ) : (
                        <>
                            <img src="/images/defaultimg.png" alt="Awaiting image" />
                            <span className="recent-card-no-image-label">Awaiting Image</span>
                        </>
                    )}
                    {featured && <span className="home-featured-badge">★ Featured</span>}
                    <div className="recent-card-overlay">
                        <i className="bx bx-search"></i>
                    </div>
                </div>
                <div className="recent-card-body">
                    <p className="recent-card-name">{product.name}</p>
                    {product.price_on_application ? (
                        <p className="recent-card-price">POA</p>
                    ) : product.price ? (
                        <p className="recent-card-price">£{parseFloat(product.price).toFixed(2)}</p>
                    ) : null}
                </div>
            </Link>
        );
    };

    return (
        <div className="home-page">

            {/* ===== HERO BANNER ===== */}
            <div className="home-hero">
                <img src="/images/Hero.jpg" alt="Plough Breakers" />
                <div className="home-hero-overlay"></div>
                <div className="home-hero-content">
                    <div className="home-hero-inner">
                        <p className="home-hero-eyebrow">Specialist Kverneland Parts</p>
                        <h1 className="home-hero-title">Plough Breakers</h1>
                        <div className="home-hero-rule"></div>
                        <p className="home-hero-sub">
                            We strip and supply quality used parts — at the right price.
                        </p>
                        <div className="home-hero-buttons">
                            <Link to="/category/2" className="hero-btn-red">Browse Parts</Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== INTRO SECTION ===== */}
            <div className="home-intro">
                <div className="home-intro-inner">
                    <div className="home-intro-text">
                        <h2 className="home-intro-title">Plough Breakers</h2>
                        <p className="home-intro-tagline">Your Specialist Kverneland Parts Supplier</p>
                        <p className="home-intro-body">
                            Specialists in breaking Kverneland ploughs for quality used parts. We strip and supply direct — so you get the right part, at the right price, without the wait. From a single component to a full set, we have the stock and knowledge to help.
                        </p>
                        <div className="home-intro-actions">
                            <Link to="/category/2" className="home-intro-btn">Browse Parts</Link>
                            <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" className="home-intro-btn-whatsapp">
                                <i className="fab fa-whatsapp" /> WhatsApp Enquiry
                            </a>
                        </div>
                    </div>
                    <div className="home-intro-image">
                        <img src="/images/rusty.jpg" alt="Kverneland plough parts" />
                    </div>
                </div>
            </div>

            {/* ===== FEATURED PRODUCTS ===== */}
            {featuredProducts.length > 0 && (
                <div className="home-featured">
                    <div className="home-featured-inner">
                        <div className="home-section-header">
                            <h2 className="home-section-title">★ Featured Products</h2>
                            <p className="home-section-sub"></p>
                        </div>
                        <div className="home-featured-grid">
                            {featuredProducts.map((product) => (
                                <ProductCard key={product.id} product={product} featured />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== CATEGORY GRID ===== */}
            <div className="home-container">
                <div className="home-container-inner">
                    <h2 className="home-title">Plough Parts</h2>
                    <p className="home-subtitle">Browse our range of quality plough parts and spares.</p>
                    <div className="home-category-grid">
                        {subcategories.map((cat) => (
                            <Link key={cat.id} to={`/category/${cat.id}`} className="home-category-card">
                                <div className="home-category-image-wrap">
                                    {cat.image_url ? (
                                        <img src={cat.image_url} alt={cat.name} className="home-category-image" />
                                    ) : (
                                        <div className="home-category-no-image">No Image</div>
                                    )}
                                    <div className="home-category-overlay">
                                        <i className="bx bx-search"></i>
                                    </div>
                                </div>
                                <div className="home-category-body">
                                    <h2>{cat.name}</h2>
                                </div>
                            </Link>
                        ))}
                    </div>
                    {subcategories.length === 0 && (
                        <p style={{ color: '#555', marginTop: '20px' }}>No categories found.</p>
                    )}
                </div>
            </div>

            {/* ===== RECENTLY ADDED ===== */}
            {recentProducts.length > 0 && (
                <div className="home-recent">
                    <div className="home-recent-inner">
                        <h2 className="home-recent-title">Recently Added</h2>
                        <p className="home-recent-sub">The latest parts added to our stock</p>
                        <Swiper
                            modules={[Pagination, Autoplay]}
                            spaceBetween={16}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            style={{ width: '100%' }}
                            breakpoints={{
                                600: { slidesPerView: 2, spaceBetween: 16 },
                                900: { slidesPerView: 3, spaceBetween: 20 },
                            }}
                            className="home-recent-swiper"
                        >
                            {recentProducts.map((product) => (
                                <SwiperSlide key={product.id}>
                                    <ProductCard product={product} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            )}

            {/* ===== TRACTOR STRIP ===== */}
            <div className="home-tractor-strip">
                {/* Hidden img used as canvas image source */}
                <img
                    ref={tractorRef}
                    src="/images/PloughBreakersLogo-black-annimate.svg"
                    alt=""
                    style={{ display: 'none' }}
                />
                <canvas ref={canvasRef} className="home-tractor-canvas" />
            </div>

        </div>
    );
}

export default Home;